import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Copy, Check, AlertTriangle, UserPlus, X } from 'lucide-react';
import { veritas, fetchAllPages, supabase } from '@/lib/supabase';
import { cn, timeAgo } from '@/lib/utils';
import { Panel, PanelBody, PanelHead } from '@/components/ui';
import type {
  AssignableUser,
  CredentialCreated,
  CredentialOwner,
  EaCredential,
} from '@/types/veritas';

export default function CredentialsPage() {
  const [creds, setCreds] = useState<EaCredential[]>([]);
  const [owners, setOwners] = useState<CredentialOwner[]>([]);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Issuance form
  const [account, setAccount] = useState('');
  const [label, setLabel] = useState('default');
  const [issuing, setIssuing] = useState(false);
  const [justCreated, setJustCreated] = useState<CredentialCreated | null>(null);
  const [copied, setCopied] = useState(false);

  // Per-row owner-assignment menu
  const [assigningId, setAssigningId] = useState<string | null>(null);

  async function load() {
    // Both reads are RLS-scoped, but for an admin that scope is EVERY
    // customer's credentials — the list outgrows the 1000-row server cap as
    // the business scales, so both must be paginated. fetchAllPages requires
    // a total-order ORDER BY: id for ea_credentials, (credential_id,user_id)
    // for credential_owners.
    const [credRes, ownerRes] = await Promise.all([
      fetchAllPages<EaCredential>((from, to) =>
        veritas
          .from('ea_credentials')
          .select(
            'id,account_login,install_label,licensed_account_id,owner_user_id,veritas_optin,created_at,last_seen_at,revoked_at',
          )
          .order('created_at', { ascending: false })
          .order('id', { ascending: true }) // unique tiebreaker — see fetchAllPages contract
          .range(from, to)),
      fetchAllPages<CredentialOwner>((from, to) =>
        veritas
          .from('credential_owners')
          .select('credential_id,user_id,email')
          .order('credential_id', { ascending: true })
          .order('user_id', { ascending: true }) // (credential_id,user_id) total order — see fetchAllPages contract
          .range(from, to)),
    ]);
    setLoading(false);
    if (credRes.error) {
      setError(credRes.error.message);
      return;
    }
    setCreds((credRes.data ?? []) as EaCredential[]);
    setOwners((ownerRes.data ?? []) as CredentialOwner[]);
  }

  // Load assignable users once on mount. Empty list = caller is not admin
  // (RPC returns nothing for non-admins by design — no error, just no rows),
  // in which case the assign UI hides itself.
  useEffect(() => {
    let cancelled = false;
    veritas.rpc('list_assignable_users').then((r) => {
      if (cancelled) return;
      setUsers((r.data ?? []) as AssignableUser[]);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setMyUserId(data.session?.user.id ?? null));
  }, []);

  useEffect(() => { load(); }, []);

  async function issue(e: FormEvent) {
    e.preventDefault();
    const accNum = Number(account);
    if (!Number.isFinite(accNum) || accNum <= 0) {
      setError('Account login must be a positive integer.');
      return;
    }
    setIssuing(true);
    setError(null);
    const { data, error } = await veritas.rpc('create_ea_credential', {
      p_account_login: accNum,
      p_install_label: label.trim() || 'default',
      p_licensed_account_id: null,
    });
    setIssuing(false);
    if (error) {
      setError(error.message);
      return;
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (row) {
      setJustCreated(row as CredentialCreated);
      setAccount('');
      setLabel('default');
      load();
    }
  }

  async function revoke(id: string) {
    if (!confirm('Revoke this credential? The EA using it will stop being accepted.')) return;
    const { error } = await veritas
      .from('ea_credentials')
      .update({ revoked_at: new Date().toISOString() })
      .eq('id', id);
    if (error) setError(error.message);
    else load();
  }

  async function setOwner(credId: string, userId: string | null) {
    setAssigningId(null);
    const { error } = await veritas
      .from('ea_credentials')
      .update({ owner_user_id: userId })
      .eq('id', credId);
    if (error) setError(error.message);
    else load();
  }

  // O(1) lookup of owner email by credential id
  const ownerByCred = useMemo(() => {
    const m = new Map<string, CredentialOwner>();
    for (const o of owners) m.set(o.credential_id, o);
    return m;
  }, [owners]);

  const isAdminUI = users.length > 0;  // RPC returned rows ⇒ caller is admin

  // ea_credentials read RLS now also returns accounts SHARED with you (account_grants),
  // but the Credentials page is for managing YOUR OWN installs. Non-admins therefore see
  // only credentials they own; admins keep the full fleet view (and the assignment UI).
  const visibleCreds = isAdminUI ? creds : creds.filter((c) => c.owner_user_id === myUserId);

  return (
    <div className="space-y-3.5">
      <div>
        <h1 className="text-[20px] font-bold tracking-tight">EA Credentials</h1>
        <p className="mt-1 text-[12px] text-text-dim">
          Each Veritas Bridge install needs a unique signing secret. Issue one per customer per
          MT5 account.
        </p>
      </div>

      {/* ── Issue new ──────────────────────────────────────────── */}
      <Panel>
        <PanelHead title="Issue new credential" />
        <PanelBody>
          <form onSubmit={issue} className="flex flex-wrap items-end gap-3">
            <label className="min-w-[200px] flex-1 text-sm">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">
                MT5 account login
              </div>
              <input
                type="text"
                inputMode="numeric"
                required
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                placeholder="37313754"
                className="num w-full rounded-md border border-line bg-bg-2 px-3 py-2 text-[13px] text-text outline-none transition-colors placeholder:text-text-faint focus:border-green"
              />
            </label>
            <label className="min-w-[200px] flex-1 text-sm">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">
                Install label
              </div>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Roboforex Cent account"
                className="w-full rounded-md border border-line bg-bg-2 px-3 py-2 text-[13px] text-text outline-none transition-colors placeholder:text-text-faint focus:border-green"
              />
            </label>
            <button
              type="submit"
              disabled={issuing}
              className="rounded-md bg-green px-4 py-2 text-[13px] font-semibold text-bg transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {issuing ? 'Issuing…' : 'Issue'}
            </button>
          </form>

          {justCreated && (
            <div className="mt-4 rounded-md border border-amber/40 bg-amber/[0.06] p-4">
              <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-amber">
                <AlertTriangle size={14} />
                Copy this signing secret now — it will not be shown again.
              </div>
              <div className="flex items-center gap-2 rounded-md bg-bg px-3 py-2 font-mono text-[12px]">
                <code className="num flex-1 break-all text-text">{justCreated.signing_secret}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(justCreated.signing_secret);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                  className="shrink-0 rounded border border-line p-1.5 text-text-mid hover:bg-bg-1 hover:text-text"
                  title="Copy"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
              <button
                onClick={() => setJustCreated(null)}
                className="mt-3 text-[10.5px] text-text-dim hover:text-text"
              >
                I&apos;ve saved it — dismiss
              </button>
            </div>
          )}
        </PanelBody>
      </Panel>

      {error && (
        <div className="rounded-md border border-red/40 bg-red/10 px-3 py-2 text-[12px] text-red">
          {error}
        </div>
      )}

      {/* ── Existing credentials ──────────────────────────────── */}
      <Panel>
        <PanelHead title={<>Existing <span className="text-text-dim">{visibleCreds.length}</span></>} />
        <PanelBody flush>
          <div className="thin-scroll overflow-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr>
                  <Th>Account</Th>
                  <Th>Label</Th>
                  <Th>Owner</Th>
                  <Th>Created</Th>
                  <Th>Last seen</Th>
                  <Th>Status</Th>
                  <Th>{''}</Th>
                </tr>
              </thead>
              <tbody>
                {visibleCreds.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-text-dim">
                      No credentials yet.
                    </td>
                  </tr>
                )}
                {visibleCreds.map((c) => {
                  const owner = ownerByCred.get(c.id);
                  return (
                    <tr key={c.id} className="border-b border-line-soft hover:bg-white/[0.02]">
                      <td className="num px-4 py-3 font-medium text-text">{c.account_login}</td>
                      <td className="px-4 py-3 text-text-mid">{c.install_label}</td>
                      <td className="px-4 py-3">
                        {owner?.email ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="text-text">{owner.email}</span>
                            {isAdminUI && !c.revoked_at && (
                              <button
                                onClick={() => setAssigningId(assigningId === c.id ? null : c.id)}
                                className="text-[10px] uppercase tracking-eyebrow text-text-dim hover:text-text"
                              >
                                Change
                              </button>
                            )}
                          </span>
                        ) : (
                          isAdminUI && !c.revoked_at ? (
                            <button
                              onClick={() => setAssigningId(assigningId === c.id ? null : c.id)}
                              className="inline-flex min-h-[40px] items-center gap-1.5 px-2 text-[11px] uppercase tracking-eyebrow text-text-dim hover:text-green md:min-h-0 md:px-0 md:text-[10.5px]"
                            >
                              <UserPlus size={12} /> Assign
                            </button>
                          ) : (
                            <span className="text-text-dim">—</span>
                          )
                        )}
                        {assigningId === c.id && isAdminUI && (
                          <OwnerPicker
                            users={users}
                            currentId={c.owner_user_id}
                            onPick={(uid) => setOwner(c.id, uid)}
                            onClose={() => setAssigningId(null)}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-mid">{timeAgo(c.created_at)}</td>
                      <td className="px-4 py-3 text-text-mid">{timeAgo(c.last_seen_at)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'rounded-[3px] px-2 py-0.5 text-[10px] font-bold uppercase tracking-eyebrow',
                            c.revoked_at
                              ? 'bg-red/10 text-red'
                              : c.veritas_optin
                                ? 'bg-green/10 text-green'
                                : 'bg-text-faint/20 text-text-dim',
                          )}
                        >
                          {c.revoked_at ? 'Revoked' : c.veritas_optin ? 'Active' : 'Opted out'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!c.revoked_at && (
                          <button
                            onClick={() => revoke(c.id)}
                            className="inline-flex min-h-[40px] items-center px-2 text-[11px] uppercase tracking-eyebrow text-text-dim hover:text-red md:min-h-0 md:px-0 md:text-[10.5px]"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// OwnerPicker — small inline dropdown for choosing or unassigning a user.
// Renders below the cell that opened it. Closes on selection or X.
function OwnerPicker({
  users,
  currentId,
  onPick,
  onClose,
}: {
  users: AssignableUser[];
  currentId: string | null;
  onPick: (userId: string | null) => void;
  onClose: () => void;
}) {
  return (
    <div className="absolute z-10 mt-2 w-[260px] rounded-md border border-line bg-bg-1 shadow-2xl">
      <div className="flex items-center justify-between border-b border-line-soft px-3 py-2">
        <span className="text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">
          Assign owner
        </span>
        <button onClick={onClose} className="text-text-dim hover:text-text">
          <X size={12} />
        </button>
      </div>
      <div className="thin-scroll max-h-56 overflow-auto py-1">
        {users.length === 0 && (
          <div className="px-3 py-3 text-[11px] text-text-dim">No users available.</div>
        )}
        {users.map((u) => (
          <button
            key={u.id}
            onClick={() => onPick(u.id)}
            className={cn(
              'block w-full truncate px-3 py-1.5 text-left text-[12px] hover:bg-bg-2',
              u.id === currentId ? 'text-green' : 'text-text',
            )}
          >
            {u.email ?? u.id.slice(0, 8)}
            {u.id === currentId && <span className="ml-2 text-[9px] uppercase tracking-eyebrow text-green">current</span>}
          </button>
        ))}
      </div>
      {currentId && (
        <button
          onClick={() => onPick(null)}
          className="block w-full border-t border-line-soft px-3 py-2 text-left text-[10.5px] uppercase tracking-eyebrow text-red hover:bg-bg-2"
        >
          Unassign
        </button>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="sticky top-0 z-[1] whitespace-nowrap border-b border-line bg-bg-2 px-4 py-2.5 text-left text-[9.5px] font-bold uppercase tracking-eyebrow text-text-dim">
      {children}
    </th>
  );
}
